import { useEffect, useRef, useState } from 'react';

import { Sort } from '@vimeo/comments-ui';
import { useGetAlbumVideoComments } from '@vimeo/gctl-api/hooks/useAlbumVideoComments';
import { useGetVideoComments } from '@vimeo/gctl-api/hooks/useVideoComments';
import { useGetVideoPrivateComments } from '@vimeo/gctl-api/hooks/useVideoPrivateComments';
import { getPasswordHashFromCookie } from '@vimeo/use-unlocked-video';

import {
  extractDeeplinkNoteId,
  getDeepLinkPublicCommentsQuery,
  getReviewPasswordHashFromCookie,
  getShowcasePasswordHashFromCookie,
} from '../../utils/helper';

type Option = {
  shouldFetch: boolean;
};

/**
 * A customized hook to to return the page that the deeplinked comment is on
 */
export function useGetCommentsDeeplink(
  clipRequestId: string,
  isPublic: boolean,
  sort?: Sort,
  showcaseId?: number,
  reviewId?: string,
) {
  const deeplinkNoteId = extractDeeplinkNoteId();

  const publicDeepLink = usePublicComments(
    clipRequestId,
    deeplinkNoteId,
    sort,
    {
      shouldFetch: isPublic,
    },
    showcaseId,
  );
  const privateDeepLink = usePrivateComments(
    clipRequestId,
    deeplinkNoteId,
    sort,
    {
      shouldFetch: !isPublic,
    },
    reviewId,
  );

  return isPublic ? publicDeepLink : privateDeepLink;
}

export function usePublicComments(
  clipRequestId: string,
  deeplinkNoteId?: number,
  sort?: Sort,
  option?: Option,
  showcaseId?: number,
) {
  const [deeplinkDataFetched, setDeeplinkDataFetched] = useState(false);

  const videoComments = useGetVideoComments(() => {
    if (deeplinkDataFetched || !deeplinkNoteId || !option?.shouldFetch || showcaseId) return null;
    const password = getPasswordHashFromCookie(clipRequestId);

    return {
      query: getDeepLinkPublicCommentsQuery(deeplinkNoteId, password, sort),
      where: { videoId: clipRequestId as unknown as number },
      select: ['uri'],
    };
  });

  const albumVideoComments = useGetAlbumVideoComments(() => {
    if (deeplinkDataFetched || !deeplinkNoteId || !option?.shouldFetch || !showcaseId) return null;
    const password = getShowcasePasswordHashFromCookie(showcaseId);

    return {
      query: getDeepLinkPublicCommentsQuery(deeplinkNoteId, password, sort),
      where: { albumId: showcaseId, videoId: clipRequestId as unknown as number },
      select: ['uri'],
    };
  });

  const { data: pageToFetchOnLoadData, error } = showcaseId ? albumVideoComments : videoComments;

  useEffect(() => {
    if (pageToFetchOnLoadData && !deeplinkDataFetched) {
      setDeeplinkDataFetched(true);
      pageRef.current = pageToFetchOnLoadData.page;
      // pageToFetchOnLoadData.page keeps going back to null after the call is finished
      // So we use pageRef.current to store the value
    }
  }, [pageToFetchOnLoadData, deeplinkDataFetched]);

  const pageRef = useRef(pageToFetchOnLoadData?.page);

  if (!deeplinkNoteId)
    return {
      initialPage: 1,
      initialPageLoading: false,
      deeplinkNoteId: null,
      deeplinkDataError: false,
    };

  return {
    initialPage: pageRef.current,
    initialPageLoading: !deeplinkDataFetched,
    deeplinkNoteId,
    deeplinkDataError: error,
  };
}

/**
 * A hook to return the page that the deeplinked comment is on for private comments
 */
export function usePrivateComments(
  clipRequestId: string,
  deeplinkNoteId?: number,
  sort?: Sort,
  option?: Option,
  reviewId?: string,
) {
  const [deeplinkDataFetched, setDeeplinkDataFetched] = useState(false);
  const password = getReviewPasswordHashFromCookie(reviewId);

  const { data: pageToFetchOnLoadData, error } = useGetVideoPrivateComments(() => {
    if (deeplinkDataFetched || !deeplinkNoteId || !option?.shouldFetch) return null;
    return {
      query: {
        noteId: deeplinkNoteId,
        sort: sort?.type,
        direction: sort?.direction,
        includeDeletedComments: true,
        status: 'open',
        reviewId,
        password,
      },
      where: { videoId: clipRequestId as unknown as number },
      select: ['uri'],
    };
  });

  useEffect(() => {
    if (pageToFetchOnLoadData && !deeplinkDataFetched) {
      setDeeplinkDataFetched(true);
      pageRef.current = pageToFetchOnLoadData.page;
      // pageToFetchOnLoadData.page keeps going back to null after the call is finished
      // So we use pageRef.current to store the value
    }
  }, [pageToFetchOnLoadData, deeplinkDataFetched]);

  const pageRef = useRef(pageToFetchOnLoadData?.page);

  if (!deeplinkNoteId)
    return {
      initialPage: 1,
      initialPageLoading: false,
      deeplinkNoteId: null,
      deeplinkDataError: false,
    };

  return {
    initialPage: pageRef.current,
    initialPageLoading: !deeplinkDataFetched,
    deeplinkNoteId,
    deeplinkDataError: error,
  };
}
